import { getStringLiterals } from '../utils/documentation';
import { DurationUnits, FunctionDefinition, T, oneOf, optional, variadic } from './definition';

export enum FunctionSections {
  LogicFunctions = 'Logic functions',
  ControlFlow = 'Control flow',
  MathematicalFunctions = 'Mathematical functions',
  StringFunctions = 'String functions',
  DateFunctions = 'Date functions',
  DateUtilityFunctions = 'Date utility functions',
  JSONFunctions = 'JSON functions',
  TypeConversions = 'Type conversions',
  WindowFunctions = 'Window functions',
  UtilityFunctions = 'Utility functions',
}

const anyNumberType = oneOf(T.number, T.float, T.integer);

const comparisonSignatures = [
  { arguments: [oneOf(T.string, T.enum), oneOf(T.string, T.enum)], returnType: T.boolean },
  { arguments: [oneOf(T.number, T.float, T.integer), oneOf(T.number, T.float, T.integer)], returnType: T.boolean },
  { arguments: [T.boolean, T.boolean], returnType: T.boolean },
  { arguments: [T.date, T.date], returnType: T.boolean },
];

const logicFunctions = {
  and: {
    name: 'and',
    signatures: [{ arguments: [variadic(T.boolean)], returnType: T.boolean }],
    description: 'Returns true if all the given values are true, false otherwise.',
    documentation: { section: FunctionSections.LogicFunctions },
  },
  or: {
    name: 'or',
    signatures: [{ arguments: [variadic(T.boolean)], returnType: T.boolean }],
    description: 'Returns true if at least one of the given values is true, false otherwise.',
    documentation: { section: FunctionSections.LogicFunctions },
  },
  not: {
    name: 'not',
    signatures: [{ arguments: [T.boolean], returnType: T.boolean }],
    description: 'Returns true if the given value is false, false otherwise.',
    documentation: { section: FunctionSections.LogicFunctions },
  },

  eq: {
    name: 'eq',
    operator: '==',
    signatures: comparisonSignatures,
    documentation: { section: FunctionSections.LogicFunctions },
    description: 'Equality check. Returns true if two given values are equal, false otherwise.',
  },
  neq: {
    name: 'neq',
    operator: '!=',
    signatures: comparisonSignatures,
    documentation: { section: FunctionSections.LogicFunctions },
    description: 'Non-equality check. Returns false if two given values are equal, true otherwise.',
  },
  gt: {
    name: 'gt',
    operator: '>',
    signatures: comparisonSignatures,
    documentation: { section: FunctionSections.LogicFunctions },
    description: 'Returns true if the first value is greater than the second, false otherwise.',
  },
  gte: {
    name: 'gte',
    operator: '>=',
    signatures: comparisonSignatures,
    documentation: { section: FunctionSections.LogicFunctions },
    description: 'Returns true if the first value is greater than or equal to the second, false otherwise.',
  },
  lt: {
    name: 'lt',
    operator: '<',
    signatures: comparisonSignatures,
    documentation: { section: FunctionSections.LogicFunctions },
    description: 'Returns true if the first value is less than the second, false otherwise.',
  },
  lte: {
    name: 'lte',
    operator: '<=',
    signatures: comparisonSignatures,
    documentation: { section: FunctionSections.LogicFunctions },
    description: 'Returns true if the first value is less than or equal to the second, false otherwise.',
  },
};

const controlFlowFunctions = {
  case: {
    name: 'case',
    signatures: [
      { arguments: [variadic(T.boolean, oneOf(T.string, T.enum)), optional(T.string)], returnType: T.string },
      { arguments: [variadic(T.boolean, anyNumberType), optional(anyNumberType)], returnType: T.number },
      { arguments: [variadic(T.boolean, T.float), optional(T.float)], returnType: T.float },
      { arguments: [variadic(T.boolean, T.integer), optional(T.integer)], returnType: T.integer },
      { arguments: [variadic(T.boolean, T.date), optional(T.date)], returnType: T.date },
      { arguments: [variadic(T.boolean, T.interval), optional(T.interval)], returnType: T.interval },
      { arguments: [variadic(T.boolean, T.boolean), optional(T.boolean)], returnType: T.boolean },
    ],
    description:
      'Returns the first value that matches its preceding condition. An optional value is returned if it is provided and none of the conditions match.',
    documentation: { section: FunctionSections.ControlFlow },
    arguments: [
      {
        variadic: [
          { name: 'condition', description: 'Condition to match' },
          { name: 'value', description: 'Value to return if the preceding condition is true' },
        ],
      },
      { name: 'default', description: 'Value to return if no conditions match', optional: true },
    ],
  },
};

const mathFunctions = {
  add: {
    name: 'add',
    signatures: [
      { arguments: [variadic(T.number)], returnType: T.number },
      { arguments: [variadic(T.float)], returnType: T.float },
      { arguments: [variadic(T.integer)], returnType: T.integer },
      { arguments: [variadic(oneOf(T.number, T.float, T.integer))], returnType: T.number },
      { arguments: [variadic(T.interval)], returnType: T.interval },
      { arguments: [T.date, T.interval], returnType: T.date },
      { arguments: [T.interval, T.date], returnType: T.date },
    ],
    description: 'Returns the sum of given values.',
    documentation: { section: FunctionSections.MathematicalFunctions },
  },
  sub: {
    name: 'sub',
    signatures: [
      { arguments: [variadic(T.number)], returnType: T.number },
      { arguments: [variadic(T.float)], returnType: T.float },
      { arguments: [variadic(T.integer)], returnType: T.integer },
      { arguments: [variadic(oneOf(T.number, T.float, T.integer))], returnType: T.number },
      { arguments: [T.date, T.date], returnType: T.interval },
      { arguments: [T.date, T.interval], returnType: T.date },
      { arguments: [T.interval, T.interval], returnType: T.interval },
    ],
    description: 'Returns the subtraction of given values.',
    documentation: { section: FunctionSections.MathematicalFunctions },
  },
  mul: {
    name: 'mul',
    signatures: [
      { arguments: [variadic(T.number)], returnType: T.number },
      { arguments: [variadic(T.float)], returnType: T.float },
      { arguments: [variadic(T.integer)], returnType: T.integer },
      { arguments: [variadic(oneOf(T.number, T.float, T.integer))], returnType: T.number },
      { arguments: [T.interval, T.number], returnType: T.interval },
      { arguments: [T.number, T.interval], returnType: T.interval },
    ],
    description: 'Returns the multiplication of given values.',
    documentation: { section: FunctionSections.MathematicalFunctions },
  },
  div: {
    name: 'div',
    signatures: [
      { arguments: [variadic(T.number)], returnType: T.number },
      { arguments: [variadic(T.float)], returnType: T.float },
      { arguments: [variadic(T.integer)], returnType: T.integer },
      { arguments: [variadic(oneOf(T.number, T.float, T.integer))], returnType: T.number },
      { arguments: [T.interval, T.number], returnType: T.interval },
    ],
    description: 'Returns the division of given values.',
    documentation: { section: FunctionSections.MathematicalFunctions },
  },
  max: {
    name: 'max',
    signatures: [
      { arguments: [variadic(T.number)], returnType: T.number },
      { arguments: [variadic(T.float)], returnType: T.float },
      { arguments: [variadic(T.integer)], returnType: T.integer },
      { arguments: [variadic(oneOf(T.number, T.float, T.integer))], returnType: T.number },
      { arguments: [variadic(T.date)], returnType: T.date },
      { arguments: [variadic(T.interval)], returnType: T.interval },
    ],
    description: 'Returns the maximum value from the given values.',
    documentation: { section: FunctionSections.MathematicalFunctions },
  },
  min: {
    name: 'min',
    signatures: [
      { arguments: [variadic(T.number)], returnType: T.number },
      { arguments: [variadic(T.float)], returnType: T.float },
      { arguments: [variadic(T.integer)], returnType: T.integer },
      { arguments: [variadic(oneOf(T.number, T.float, T.integer))], returnType: T.number },
      { arguments: [variadic(T.date)], returnType: T.date },
      { arguments: [variadic(T.interval)], returnType: T.interval },
    ],
    description: 'Returns the minimum value from the given values.',
    documentation: { section: FunctionSections.MathematicalFunctions },
  },
  ceil: {
    name: 'ceil',
    signatures: [{ arguments: [oneOf(T.number, T.float, T.integer)], returnType: T.number }],
    description: 'Returns the smallest integer greater than or equal to the given value.',
    documentation: { section: FunctionSections.MathematicalFunctions },
  },
  floor: {
    name: 'floor',
    signatures: [{ arguments: [oneOf(T.number, T.float, T.integer)], returnType: T.number }],
    description: 'Returns the largest integer less than or equal to the given value.',
    documentation: { section: FunctionSections.MathematicalFunctions },
  },
  round: {
    name: 'round',
    signatures: [
      { arguments: [oneOf(T.number, T.float, T.integer), T.number], returnType: T.number },
      { arguments: [oneOf(T.number, T.float, T.integer)], returnType: T.number },
    ],
    description: 'Returns the value rounded to the nearest integer.',
    documentation: { section: FunctionSections.MathematicalFunctions },
  },
};

const stringFunctions = {
  contains: {
    name: 'contains',
    // TODO: First argument can only be field in Postgres, for example
    signatures: [
      { arguments: [T.string, T.string], returnType: T.boolean },
      { arguments: [T.enum, T.string], returnType: T.boolean },
    ],
    description: 'Returns true if the first value contains the second value (case-insensitive), false otherwise.',
    documentation: { section: FunctionSections.StringFunctions },
  },
  split_part: {
    name: 'split_part',
    signatures: [{ arguments: [oneOf(T.string, T.enum), T.string, T.number], returnType: T.string }],
    description:
      'Splits the given string by the given delimiter and returns the part at the given position. The position is 1-based. If the position is greater than the number of parts, a null value is returned.',
    documentation: { section: FunctionSections.StringFunctions },
  },
  concat: {
    name: 'concat',
    signatures: [
      { arguments: [variadic(oneOf(T.string, T.number, T.float, T.integer, T.enum))], returnType: T.string },
    ],
    description: 'Concatenates given values into a single string.',
    documentation: { section: FunctionSections.StringFunctions },
  },
  len: {
    name: 'len',
    signatures: [
      { arguments: [T.string], returnType: T.number },
    ],
    description: 'Returns the length of the string from the given value.',
    documentation: { section: FunctionSections.StringFunctions },
  },
};

const dateExtractSignatures = [{ arguments: [T.date], returnType: T.number }];
const dateFunctions = {
  date: {
    name: 'date',
    signatures: [
      {
        arguments: [oneOf(T.string, T.date)],
        returnType: T.date,
      },
    ],
    description: 'Returns the given value as a Date.',
    documentation: { section: FunctionSections.DateFunctions },
  },
  now: {
    name: 'now',
    signatures: [{ arguments: [], returnType: T.date }],
    description: 'Returns the current date and time.',
    documentation: { section: FunctionSections.DateFunctions },
  },
  year: {
    name: 'year',
    signatures: dateExtractSignatures,
    documentation: { section: FunctionSections.DateFunctions },
    description: 'Extracts the year from the given date value.',
  },
  month: {
    name: 'month',
    signatures: dateExtractSignatures,
    documentation: { section: FunctionSections.DateFunctions },
    description: 'Extracts the month from the given date value.',
  },
  week: {
    name: 'week',
    signatures: dateExtractSignatures,
    documentation: { section: FunctionSections.DateFunctions },
    description: 'Extracts the week number from the given date value.',
  },
  day: {
    name: 'day',
    signatures: dateExtractSignatures,
    documentation: { section: FunctionSections.DateFunctions },
    description: 'Extracts the day from the given date value.',
  },
  hour: {
    name: 'hour',
    signatures: dateExtractSignatures,
    documentation: { section: FunctionSections.DateFunctions },
    description: 'Extracts the hour from the given date value.',
  },
  minute: {
    name: 'minute',
    signatures: dateExtractSignatures,
    documentation: { section: FunctionSections.DateFunctions },
    description: 'Extracts the minute from the given date value.',
  },
  second: {
    name: 'second',
    signatures: dateExtractSignatures,
    documentation: { section: FunctionSections.DateFunctions },
    description: 'Extracts the second from the given date value.',
  },
  day_of_week: {
    name: 'day_of_week',
    signatures: dateExtractSignatures,
    documentation: { section: FunctionSections.DateFunctions },
    description: 'Extracts the day of the week from the given date value.',
  },
};

const dateUtilityFunctions = {
  interval: {
    name: 'interval',
    signatures: [{ arguments: [T.number, T.string], returnType: T.interval }],
    description: 'Creates an interval value of the given duration.',
    documentation: { section: FunctionSections.DateUtilityFunctions },
    arguments: [
      { name: 'count', description: 'How many periods in the interval' },
      {
        name: 'period',
        description: 'Length of one period',
        values: getStringLiterals(DurationUnits),
      },
    ],
  },
  extract: {
    name: 'extract',
    signatures: [{ arguments: [T.interval, T.string], returnType: T.number }],
    description: 'Extracts given date part from the given interval.',
    documentation: { section: FunctionSections.DateUtilityFunctions },
    arguments: [
      { name: 'interval', description: 'Interval value' },
      {
        name: 'field',
        description: 'Date part to extract',
        values: getStringLiterals(DurationUnits),
      },
    ],
  },

  date_diff: {
    name: 'date_diff',
    signatures: [{ arguments: [T.date, T.date, T.string], returnType: T.number }],
    description: 'Returns the difference between two dates in the given units.',
    documentation: { section: FunctionSections.DateUtilityFunctions },
    arguments: [
      { name: 'start', description: 'Start date' },
      { name: 'end', description: 'End date' },
      {
        name: 'unit',
        description: 'Unit of the result',
        values: getStringLiterals(DurationUnits),
      },
    ],
  },

  start_of_year: {
    name: 'start_of_year',
    signatures: [{ arguments: [T.date], returnType: T.date }],
    description: 'Returns the given date value truncated to the start of the year.',
    documentation: { section: FunctionSections.DateUtilityFunctions },
  },
  end_of_year: {
    name: 'end_of_year',
    signatures: [{ arguments: [T.date], returnType: T.date }],
    description: 'Return the given date value truncated to the end of the year / start of next year.',
    documentation: { section: FunctionSections.DateUtilityFunctions },
  },
  start_of_quarter: {
    name: 'start_of_quarter',
    signatures: [{ arguments: [T.date], returnType: T.date }],
    description: 'Returns the given date value truncated to the start of the quarter.',
    documentation: { section: FunctionSections.DateUtilityFunctions },
  },
  end_of_quarter: {
    name: 'end_of_quarter',
    signatures: [{ arguments: [T.date], returnType: T.date }],
    description: 'Return the given date value truncated to the end of the quarter / start of next quarter.',
    documentation: { section: FunctionSections.DateUtilityFunctions },
  },
  start_of_month: {
    name: 'start_of_month',
    signatures: [{ arguments: [T.date], returnType: T.date }],
    description: 'Returns the given date value truncated to the start of the month.',
    documentation: { section: FunctionSections.DateUtilityFunctions },
  },
  end_of_month: {
    name: 'end_of_month',
    signatures: [{ arguments: [T.date], returnType: T.date }],
    description: 'Returns the given date value truncated to the end of the month / start of next month.',
    documentation: { section: FunctionSections.DateUtilityFunctions },
  },
  start_of_week: {
    name: 'start_of_week',
    signatures: [{ arguments: [T.date], returnType: T.date }],
    description: 'Returns the given date value truncated to the start of the week.',
    documentation: { section: FunctionSections.DateUtilityFunctions },
  },
  end_of_week: {
    name: 'end_of_week',
    signatures: [{ arguments: [T.date], returnType: T.date }],
    description: 'Returns the given date value truncated to the end of the week / start of next week.',
    documentation: { section: FunctionSections.DateUtilityFunctions },
  },
  start_of_day: {
    name: 'start_of_day',
    signatures: [{ arguments: [T.date], returnType: T.date }],
    description: 'Returns the given date value truncated to the start of the day.',
    documentation: { section: FunctionSections.DateUtilityFunctions },
  },
  end_of_day: {
    name: 'end_of_day',
    signatures: [{ arguments: [T.date], returnType: T.date }],
    description: 'Returns the given date value truncated to the end of the day / start of next day.',
    documentation: { section: FunctionSections.DateUtilityFunctions },
  },
  start_of_hour: {
    name: 'start_of_hour',
    signatures: [{ arguments: [T.date], returnType: T.date }],
    description: 'Returns the given date value truncated to the start of the hour.',
    documentation: { section: FunctionSections.DateUtilityFunctions },
  },
  end_of_hour: {
    name: 'end_of_hour',
    signatures: [{ arguments: [T.date], returnType: T.date }],
    description: 'Returns the given date value truncated to the end of the hour / start of next hour.',
    documentation: { section: FunctionSections.DateUtilityFunctions },
  },
  start_of_minute: {
    name: 'start_of_minute',
    signatures: [{ arguments: [T.date], returnType: T.date }],
    description: 'Returns the given date value truncated to the start of the minute.',
    documentation: { section: FunctionSections.DateUtilityFunctions },
  },
  end_of_minute: {
    name: 'end_of_minute',
    signatures: [{ arguments: [T.date], returnType: T.date }],
    description: 'Returns the given date value truncated to the end of the minute / start of next minute.',
    documentation: { section: FunctionSections.DateUtilityFunctions },
  },
};

const jsonFunctions = {
  json_get_text: {
    name: 'json_get_text',
    signatures: [{ arguments: [T.object, T.string], returnType: T.string }],
    description: 'Returns the value of the given key from a json object as a string.',
    documentation: { section: FunctionSections.JSONFunctions },
    arguments: [
      { name: 'object', description: 'JSON object' },
      { name: 'key', description: 'Key to get the value of' },
    ],
  },
  json_get_number: {
    name: 'json_get_number',
    signatures: [{ arguments: [T.object, T.string], returnType: T.number }],
    description: 'Returns the value of the given key from a json object as a number.',
    documentation: { section: FunctionSections.JSONFunctions },
    arguments: [
      { name: 'object', description: 'JSON object' },
      { name: 'key', description: 'Key to get the value of' },
    ],
  },
  json_get_integer: {
    name: 'json_get_integer',
    signatures: [{ arguments: [T.object, T.string], returnType: T.integer }],
    description: 'Returns the value of the given key from a json object as an integer.',
    documentation: { section: FunctionSections.JSONFunctions },
    arguments: [
      { name: 'object', description: 'JSON object' },
      { name: 'key', description: 'Key to get the value of' },
    ],
  },
  json_get_float: {
    name: 'json_get_float',
    signatures: [{ arguments: [T.object, T.string], returnType: T.float }],
    description: 'Returns the value of the given key from a json object as a float.',
    documentation: { section: FunctionSections.JSONFunctions },
    arguments: [
      { name: 'object', description: 'JSON object' },
      { name: 'key', description: 'Key to get the value of' },
    ],
  },
  json_get_timestamptz: {
    name: 'json_get_timestamptz',
    signatures: [{ arguments: [T.object, T.string], returnType: T.date }],
    description: 'Returns the value of the given key from a json object as a date.',
    documentation: { section: FunctionSections.JSONFunctions },
    arguments: [
      { name: 'object', description: 'JSON object' },
      { name: 'key', description: 'Key to get the value of' },
    ],
  },
  json_get_boolean: {
    name: 'json_get_boolean',
    signatures: [{ arguments: [T.object, T.string], returnType: T.boolean }],
    description: 'Returns the value of the given key from a json object as a boolean.',
    documentation: { section: FunctionSections.JSONFunctions },
    arguments: [
      { name: 'object', description: 'JSON object' },
      { name: 'key', description: 'Key to get the value of' },
    ],
  },
};

const typeConversions = {
  int: {
    name: 'int',
    signatures: [
      {
        arguments: [oneOf(T.boolean, T.string, T.enum, T.number, T.float, T.integer)],
        returnType: T.integer,
      },
    ],
    description: 'Returns the given value as an integer.',
    documentation: { section: FunctionSections.TypeConversions },
  },

  float: {
    name: 'float',
    signatures: [
      {
        arguments: [oneOf(T.string, T.enum, T.number, T.float, T.integer)],
        returnType: T.float,
      },
    ],
    description: 'Returns the given value as a floating point number.',
    documentation: { section: FunctionSections.TypeConversions },
  },

  str: {
    name: 'str',
    signatures: [
      {
        arguments: [oneOf(T.boolean, T.string, T.enum, T.number, T.float, T.integer, T.date, T.object, T.interval)],
        returnType: T.string,
      },
    ],
    description: 'Returns the given value as a string.',
    documentation: { section: FunctionSections.TypeConversions },
  },

  bool: {
    name: 'bool',
    signatures: [
      {
        arguments: [oneOf(T.boolean, T.number, T.integer)],
        returnType: T.boolean,
      },
    ],
    description: 'Returns the given value as a boolean.',
    documentation: { section: FunctionSections.TypeConversions },
  },
};

const anyFieldType = oneOf(T.string, T.enum, T.date, T.number, T.integer, T.float, T.boolean);

const aggregationSignatures = [
  // Prop (whole column)
  { arguments: [oneOf(T.number, T.integer, T.float)], returnType: T.number },
  // Prop, sort col, sort dir (enum?), range start, range end
  {
    arguments: [oneOf(T.number, T.integer, T.float), anyFieldType, T.string, T.number, T.number],
    returnType: T.number,
  },
  // Prop, sort col, sort dir, range start, range end, group
  {
    arguments: [oneOf(T.number, T.integer, T.float), anyFieldType, T.string, T.number, T.number, anyFieldType],
    returnType: T.number,
  },
  // Prop, group
  { arguments: [oneOf(T.number, T.integer, T.float), anyFieldType], returnType: T.number },
];

const windowFunctions = {
  sum: {
    name: 'sum',
    signatures: aggregationSignatures,
    description:
      'Returns the sum of the given column or range.\nRange nomenclature: unbounded: null, current row: 0 , previous row: -1, next row: 1.',
    documentation: { section: FunctionSections.WindowFunctions },
    arguments: [
      { name: 'field', description: 'Field to aggregate' },
      { name: 'sort_field', description: 'Field to sort by', optional: true },
      { name: 'sort_direction', description: 'Sort direction ("asc"/"desc")', optional: true },
      {
        name: 'range_start',
        description: 'Start index of range to sum over, relative to the current row',
        optional: true,
      },
      { name: 'range_end', description: 'End index of range to sum over, relative to the current row', optional: true },
      { name: 'group', description: 'Field to group by', optional: true },
    ],
  },
  count: {
    name: 'count',
    signatures: [
      { arguments: [], returnType: T.number },
      // Grouping only
      { arguments: [anyFieldType], returnType: T.number },
      // Sort, direction, range start, range end
      { arguments: [anyFieldType, T.string, T.number, T.number], returnType: T.number },
      // Sort, direction, range start, range end, group
      { arguments: [anyFieldType, T.string, T.number, T.number, anyFieldType], returnType: T.number },
    ],
    description:
      'Returns the count of the given column or range.\nRange nomenclature: unbounded: null, current row: 0 , previous row: -1, next row: 1.',
    documentation: { section: FunctionSections.WindowFunctions },
    arguments: [
      { name: 'sort_field', description: 'Field to sort by', optional: true },
      { name: 'sort_direction', description: 'Sort direction ("asc"/"desc")', optional: true },
      {
        name: 'range_start',
        description: 'Start index of range to count from, relative to the current row',
        optional: true,
      },
      { name: 'range_end', description: 'End index of range to count to, relative to the current row', optional: true },
      { name: 'group', description: 'Field to group by', optional: true },
    ],
  },
  avg: {
    name: 'avg',
    signatures: aggregationSignatures,
    description:
      'Returns the average of the given values.\nRange nomenclature: unbounded: null, current row: 0 , previous row: -1, next row: 1.',
    documentation: { section: FunctionSections.WindowFunctions },
    arguments: [
      { name: 'field', description: 'Field to aggregate' },
      { name: 'sort_field', description: 'Field to sort by', optional: true },
      { name: 'sort_direction', description: 'Sort direction ("asc"/"desc")', optional: true },
      {
        name: 'range_start',
        description: 'Start index of range to average over, relative to the current row',
        optional: true,
      },
      {
        name: 'range_end',
        description: 'End index of range to average over, relative to the current row',
        optional: true,
      },
      { name: 'group', description: 'Field to group by', optional: true },
    ],
  },
  percentage: {
    name: 'percentage',
    signatures: [
      { arguments: [T.number], returnType: T.number },
      { arguments: [T.number, oneOf(T.string, T.enum, T.boolean, T.date)], returnType: T.number },
      { arguments: [T.float], returnType: T.float },
      { arguments: [T.float, oneOf(T.string, T.enum, T.boolean, T.date)], returnType: T.float },
      { arguments: [T.integer], returnType: T.integer },
      { arguments: [T.integer, oneOf(T.string, T.enum, T.boolean, T.date)], returnType: T.integer },
    ],
    description: `Returns the percentage (between 0 and 1) of the value in a row relative to the sum of the value in all rows.
      The optional second argument can be used to break up the rows into groups when calculating the percentage.`,
    documentation: { section: FunctionSections.WindowFunctions },
    arguments: [
      { name: 'value', description: 'Value to calculate the percentage of' },
      { name: 'group', description: 'Value by which to group the rows during calculation', optional: true },
    ],
  },
  offset: {
    name: 'offset',
    signatures: [
      // Column, offset, sort, direction?
      { arguments: [oneOf(T.string, T.enum), T.number, anyFieldType, optional(T.string)], returnType: T.string },
      { arguments: [T.number, T.number, anyFieldType, optional(T.string)], returnType: T.number },
      { arguments: [T.integer, T.number, anyFieldType, optional(T.string)], returnType: T.integer },
      { arguments: [T.float, T.number, anyFieldType, optional(T.string)], returnType: T.float },
      { arguments: [T.boolean, T.number, anyFieldType, optional(T.string)], returnType: T.boolean },
      { arguments: [T.date, T.number, anyFieldType, optional(T.string)], returnType: T.date },
      // Column, offset, sort, direction, group
      { arguments: [oneOf(T.string, T.enum), T.number, anyFieldType, T.string, anyFieldType], returnType: T.string },
      { arguments: [T.number, T.number, anyFieldType, T.string, anyFieldType], returnType: T.number },
      { arguments: [T.integer, T.number, anyFieldType, T.string, anyFieldType], returnType: T.integer },
      { arguments: [T.float, T.number, anyFieldType, T.string, anyFieldType], returnType: T.float },
      { arguments: [T.boolean, T.number, anyFieldType, T.string, anyFieldType], returnType: T.boolean },
      { arguments: [T.date, T.number, anyFieldType, T.string, anyFieldType], returnType: T.date },
    ],
    description: 'Returns the value of the given property offset from the current row by the given amount.',
    documentation: { section: FunctionSections.WindowFunctions },
    arguments: [
      { name: 'field', description: 'Field to offset' },
      { name: 'offset', description: 'Offset index (<0 will result in previous values, e.g. -2)' },
      { name: 'sort_field', description: 'Field to sort by' },
      { name: 'sort_direction', description: 'Sort direction ("asc"/"desc")' },
      { name: 'group', description: 'Field to group by', optional: true },
    ],
  },

  row_number: {
    name: 'row_number',
    signatures: [
      { arguments: [], returnType: T.integer },
      { arguments: [anyFieldType], returnType: T.integer }, // Group
      { arguments: [anyFieldType, T.string], returnType: T.integer }, // Sort, direction
      { arguments: [anyFieldType, T.string, anyFieldType], returnType: T.integer }, // Sort, direction, group
    ],
    description: 'Returns the row number of the row in the current group.',
    documentation: { section: FunctionSections.WindowFunctions },
    arguments: [
      { name: 'sort_field', description: 'Field to sort by', optional: true },
      { name: 'sort_direction', description: 'Sort direction ("asc"/"desc")', optional: true },
      { name: 'group', description: 'Field to group by', optional: true },
    ],
  },
};

const utilityFunctions = {
  prop: {
    name: 'prop',
    signatures: [
      { arguments: [T.string], returnType: null },
      { arguments: [T.reference], returnType: null },
    ],
    description: 'Returns the value of the given property from the current row.',
    documentation: { section: FunctionSections.UtilityFunctions },
  },

  variable: {
    name: 'variable',
    signatures: [{ arguments: [T.string], returnType: null }],
    description: 'Returns the value of the given variable in the exploration.',
    documentation: { section: FunctionSections.UtilityFunctions },
  },

  coalesce: {
    name: 'coalesce',
    signatures: [
      { arguments: [variadic(T.string)], returnType: T.string },
      { arguments: [variadic(T.number)], returnType: T.number },
      { arguments: [variadic(T.float)], returnType: T.float },
      { arguments: [variadic(T.integer)], returnType: T.integer },
      { arguments: [variadic(oneOf(T.number, T.float, T.integer))], returnType: T.number },
      { arguments: [variadic(T.boolean)], returnType: T.boolean },
      { arguments: [variadic(T.date)], returnType: T.date },
      { arguments: [variadic(T.interval)], returnType: T.interval },
      { arguments: [variadic(T.enum)], returnType: T.enum },
      { arguments: [variadic(oneOf(T.string, T.enum))], returnType: T.string },
    ],
    description: 'Returns first non-null value in arguments.',
    documentation: { section: FunctionSections.UtilityFunctions },
  },

  format: {
    name: 'format',
    signatures: [
      { arguments: [T.string, T.string], returnType: T.string },
      { arguments: [T.number, T.string], returnType: T.number },
      { arguments: [T.float, T.string], returnType: T.float },
      { arguments: [T.integer, T.string], returnType: T.integer },
      { arguments: [T.boolean, T.string], returnType: T.boolean },
      { arguments: [T.date, T.string], returnType: T.date },
      { arguments: [T.interval, T.string], returnType: T.interval },
      { arguments: [T.enum, T.string], returnType: T.enum },
    ],
    description: 'Formats the value to the given format in the final output.',
    documentation: { section: FunctionSections.UtilityFunctions },
    arguments: [
      { name: 'field', description: 'Field to format' },
      {
        name: 'format',
        description:
          'Format to display in. For all numeric fields, can be one of "eur", "usd" or "gbp". Floating-point number fields also accept "percentage". For date fields, can be one of "iso", "date" or "time".',
      },
    ],
  },

  prql: {
    name: 'prql',
    signatures: [{ arguments: [T.string], returnType: null }],
    description: 'Returns the value of the given raw PRQL query. Use this as an escape hatch for advanced use cases.',
    documentation: { section: FunctionSections.UtilityFunctions },
  },
};

export const functions: { [key: string]: FunctionDefinition } = {
  ...logicFunctions,
  ...controlFlowFunctions,
  ...mathFunctions,
  ...stringFunctions,
  ...dateFunctions,
  ...dateUtilityFunctions,
  ...jsonFunctions,
  ...typeConversions,
  ...windowFunctions,
  ...utilityFunctions,
};

export const getFunctionDefinitionByName = (name: string): FunctionDefinition | undefined => functions[name];
